import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import ShopHeader from '@/components/commerce/ShopHeader';
import ShopFooter from '@/components/commerce/ShopFooter';
import MobileBottomNav from '@/components/commerce/MobileBottomNav';
import CartDrawer from '@/components/commerce/CartDrawer';
import ProductCard from '@/components/commerce/ProductCard';
import PriceTag from '@/components/commerce/PriceTag';
import { useShopCart } from '@/context/ShopCartContext';
import { useShopFavorites } from '@/context/ShopFavoritesContext';
import { useShopOrg } from '@/context/ShopOrgContext';
import { ArrowLeft, Heart, Minus, Package, Plus, Share2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopProductDetailPage = () => {
  const { slug } = useParams();
  const { id: orgId, slug: shopSlug, name: organizationName, logoUrl, footer } = useShopOrg();
  const navigate = useNavigate();
  const { addToCart } = useShopCart();
  const { isFavorite, toggleFavorite } = useShopFavorites();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [searchValue, setSearchValue] = useState('');

  const fetchShop = useCallback(async () => {
    try {
      setLoading(true);
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/public/commerce/${orgId}/categories`),
        axios.get(`${API}/public/commerce/${orgId}/products`),
      ]);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Kon product niet laden');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchShop();
    setQuantity(1);
  }, [fetchShop, slug]);

  const product = products.find((p) => p.slug === slug);
  const categoryName = product ? (categories.find((c) => c.id === product.category_id)?.name || null) : null;
  const related = product
    ? products.filter((p) => p.id !== product.id && p.category_id === product.category_id).slice(0, 4)
    : [];

  const shareProduct = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: product.title, url });
      } catch (error) {
        // user cancelled share, nothing to do
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link gekopieerd');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-200"></div>
          <p className="text-slate-500">Product laden...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-center p-6">
        <div>
          <p className="text-slate-500 mb-4">Product niet gevonden.</p>
          <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" onClick={() => navigate(`/${shopSlug}/shop/products`)}>
            Terug naar winkel
          </Button>
        </div>
      </div>
    );
  }

  const soldOut = product.track_stock && (product.stock ?? 0) <= 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-20 sm:pb-0">
      <Toaster position="top-right" richColors />
      <ShopHeader organizationName={organizationName} logoUrl={logoUrl} searchValue={searchValue} onSearchChange={(value) => { setSearchValue(value); if (value.trim()) navigate(`/${shopSlug}/shop/products?q=${encodeURIComponent(value)}`); }} />
      <CartDrawer />

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        <button
          type="button"
          onClick={() => navigate(`/${shopSlug}/shop/products`)}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Terug naar producten
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="w-full aspect-square bg-white rounded-2xl shadow-sm flex items-center justify-center overflow-hidden group">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.title}
                className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-110"
              />
            ) : (
              <Package className="w-20 h-20 text-slate-300" />
            )}
          </div>

          <div className="space-y-4">
            {categoryName && <p className="text-sm text-teal-600 font-medium">{categoryName}</p>}
            <h1 className="text-2xl font-semibold text-slate-900">{product.title}</h1>
            <PriceTag price={product.price} salePrice={product.sale_price} size="lg" />

            {product.short_description && <p className="text-slate-600">{product.short_description}</p>}

            <p className="text-sm">
              {soldOut ? (
                <span className="text-red-600 font-medium">Niet op voorraad</span>
              ) : product.track_stock ? (
                <span className="text-emerald-600 font-medium">Op voorraad ({product.stock})</span>
              ) : (
                <span className="text-emerald-600 font-medium">Op voorraad</span>
              )}
            </p>

            <div className="flex items-center gap-3">
              <div className="flex items-center border border-slate-200 rounded-xl">
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-8 text-center">{quantity}</span>
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => setQuantity((q) => q + 1)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <Button
                className="rounded-xl bg-teal-600 hover:bg-teal-700 flex-1"
                disabled={soldOut}
                onClick={() => addToCart(product, quantity)}
              >
                Toevoegen aan winkelwagen
              </Button>
              <Button variant="outline" size="sm" className="rounded-xl h-10 w-10 p-0" onClick={() => toggleFavorite(product.id)}>
                <Heart className={`w-4 h-4 ${isFavorite(product.id) ? 'fill-red-500 text-red-500' : ''}`} />
              </Button>
              <Button variant="outline" size="sm" className="rounded-xl h-10 w-10 p-0" onClick={shareProduct}>
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <Tabs defaultValue="description">
          <div className="overflow-x-auto">
            <TabsList>
              <TabsTrigger value="description">Beschrijving</TabsTrigger>
              <TabsTrigger value="specs">Specificaties</TabsTrigger>
              <TabsTrigger value="downloads">Downloads</TabsTrigger>
              <TabsTrigger value="reviews">Beoordelingen</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="description" className="text-slate-600 whitespace-pre-wrap">
            {product.description || product.short_description || 'Geen beschrijving beschikbaar.'}
          </TabsContent>
          <TabsContent value="specs">
            <dl className="text-sm text-slate-600 space-y-1">
              {product.sku && <div><dt className="inline font-medium text-slate-900">SKU: </dt><dd className="inline">{product.sku}</dd></div>}
              {categoryName && <div><dt className="inline font-medium text-slate-900">Categorie: </dt><dd className="inline">{categoryName}</dd></div>}
              {product.tags && product.tags.length > 0 && (
                <div><dt className="inline font-medium text-slate-900">Tags: </dt><dd className="inline">{product.tags.join(', ')}</dd></div>
              )}
            </dl>
          </TabsContent>
          <TabsContent value="downloads" className="text-sm text-slate-500">
            Downloads zijn nog niet beschikbaar voor dit product.
          </TabsContent>
          <TabsContent value="reviews" className="text-sm text-slate-500">
            Beoordelingen komen binnenkort beschikbaar.
          </TabsContent>
        </Tabs>

        {related.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">Gerelateerde producten</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((item) => (
                <ProductCard key={item.id} product={item} onClick={() => navigate(`/${shopSlug}/shop/product/${item.slug}`)} />
              ))}
            </div>
          </section>
        )}
      </main>

      <ShopFooter organizationName={organizationName} footer={footer} />
      <MobileBottomNav />
    </div>
  );
};

export default ShopProductDetailPage;
